/**
 * PROCESAMIENTO PAGE — Cola de Trabajo y Captura de Resultados
 *
 * Vista principal del módulo de procesamiento.
 * Muestra la cola de trabajo por área con tabla paginada server-side
 * y un panel lateral (Sheet) para captura de resultados.
 *
 * Requirements: 6.1, 6.2, 6.7, 6.8
 */

import { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FlaskConical, RefreshCw, AlertCircle, Search, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DataTablePagination } from "@/components/ui/data-table";

import { useSucursal } from "@/contexts/sucursal-context";
import { useServerDataTable } from "@/hooks/use-server-data-table";
import {
  useContadoresArea,
  getColaTrabajo,
  type ColaTrabajoItem,
  type AreaLaboratorio,
} from "@/hooks/use-procesamiento";

import { AreaSelector } from "./components/area-selector";
import { ColaTrabajoTable } from "./components/cola-trabajo-table";
import { ResultadoCaptureSheet } from "./components/resultado-capture-sheet";

export default function ProcesamientoPage() {
  const { t } = useTranslation();
  const { sucursalActiva } = useSucursal();

  // State
  const [selectedExam, setSelectedExam] = useState<ColaTrabajoItem | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const sucursalId = sucursalActiva?.id ?? 0;

  // Area counters
  const { data: contadores = [] } = useContadoresArea(sucursalId > 0 ? sucursalId : null);

  // Build fetchFn that includes sucursal
  const fetchFn = useCallback(
    (params: Record<string, unknown>) => {
      return getColaTrabajo({
        sucursal: sucursalId,
        ...params,
      });
    },
    [sucursalId]
  );

  // Server-side data table
  const {
    table,
    data,
    isLoading,
    error,
    totalCount,
    refetch,
    searchTerm,
    setSearchTerm,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
  } = useServerDataTable<ColaTrabajoItem>({
    columns: useMemo(() => [], []), // Columns defined in ColaTrabajoTable
    fetchFn,
    // Incluir sucursalId en el queryKey para que TanStack Query refetchee
    // automáticamente cuando cambia la sucursal activa
    queryKey: `procesamiento-cola-${sucursalId}`,
    storageKey: "procesamiento_cola_page_size",
    defaultPageSize: 25,
    debounceMs: 300,
    staleTime: 0, // siempre considerar stale — la cola es datos en tiempo real
    enabled: sucursalId > 0, // Only fetch when sucursal is valid
    filters: [
      { key: "area", defaultValue: "", urlParam: "area" },
      { key: "estado", defaultValue: "all", urlParam: "estado" },
      { key: "prioridad", defaultValue: "all", urlParam: "prioridad" },
    ],
  });

  // Handlers
  const handleAreaChange = useCallback(
    (area: AreaLaboratorio | "") => {
      setFilter("area", area);
    },
    [setFilter]
  );

  const handleRowClick = useCallback((item: ColaTrabajoItem) => {
    setSelectedExam(item);
    setIsSheetOpen(true);
  }, []);

  const handleSheetClose = useCallback(() => {
    setIsSheetOpen(false);
    setSelectedExam(null);
  }, []);

  const handleCaptureSuccess = useCallback(() => {
    setIsSheetOpen(false);
    setSelectedExam(null);
    refetch();
  }, [refetch]);

  // Guard: no sucursal
  if (!sucursalId || sucursalId <= 0) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800 dark:border-yellow-800 dark:bg-yellow-950/30 dark:text-yellow-200">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{t("procesamiento.selecciona_sucursal")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <FlaskConical className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">{t("procesamiento.title")}</h1>
            <p className="text-sm text-muted-foreground">{t("procesamiento.description")}</p>
          </div>
        </div>

        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
          {t("common.refresh")}
        </Button>
      </div>

      {/* Area Selector */}
      <AreaSelector
        contadores={contadores}
        selectedArea={(filters.area as AreaLaboratorio | "") || ""}
        onAreaChange={handleAreaChange}
      />

      {/* Filters Bar */}
      <Card>
        <CardContent className="p-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 w-full sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t("procesamiento.cola.buscar_placeholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Estado filter */}
            <Select value={filters.estado || "all"} onValueChange={(v) => setFilter("estado", v)}>
              <SelectTrigger className="w-full sm:w-[160px]">
                <SelectValue placeholder={t("procesamiento.cola.filtro_estado")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("procesamiento.cola.todos_estados")}</SelectItem>
                <SelectItem value="PENDIENTE">{t("procesamiento.estados.pendiente")}</SelectItem>
                <SelectItem value="EN_PROCESO">{t("procesamiento.estados.en_proceso")}</SelectItem>
              </SelectContent>
            </Select>

            {/* Prioridad filter */}
            <Select
              value={filters.prioridad || "all"}
              onValueChange={(v) => setFilter("prioridad", v)}
            >
              <SelectTrigger className="w-full sm:w-[160px]">
                <SelectValue placeholder={t("procesamiento.cola.filtro_prioridad")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("procesamiento.cola.todas_prioridades")}</SelectItem>
                <SelectItem value="STAT">STAT</SelectItem>
                <SelectItem value="URGENTE">{t("procesamiento.prioridades.urgente")}</SelectItem>
                <SelectItem value="NORMAL">{t("procesamiento.prioridades.normal")}</SelectItem>
              </SelectContent>
            </Select>

            {/* Clear filters */}
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="h-4 w-4 mr-1" />
                {t("common.clearFilters")}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <ColaTrabajoTable data={data} isLoading={isLoading} onRowClick={handleRowClick} />
          <DataTablePagination table={table} totalRows={totalCount} />
        </CardContent>
      </Card>

      {/* Capture Sheet */}
      <ResultadoCaptureSheet
        open={isSheetOpen}
        onOpenChange={(open: boolean) => {
          if (!open) handleSheetClose();
        }}
        item={selectedExam}
        onSuccess={handleCaptureSuccess}
      />
    </div>
  );
}
